import { create } from '@bufbuild/protobuf';
import { createClient } from '@connectrpc/connect';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  GetHealthTimelineRequestSchema,
  GetServiceHealthStatusRequestSchema,
  HealthService,
  InvestigateServiceRequestSchema,
} from '../gen/health/v1/health_pb';
import { createApiTransport, createAuthApiTransport } from '../transport';

function transport(token?: string | null) {
  return token ? createAuthApiTransport(token) : createApiTransport();
}

export interface GetHealthTimelineParams {
  id: string;
  level: string;
  from: string;
  to: string;
  grain: string;
  overallOnly?: boolean;
}

export function useGetHealthTimeline(
  token?: string | null,
  params?: GetHealthTimelineParams,
  enabled = true,
) {
  return useQuery({
    queryKey: [
      'health-timeline',
      params?.id ?? '',
      params?.level ?? '',
      params?.from ?? '',
      params?.to ?? '',
      params?.grain ?? '',
      String(params?.overallOnly ?? false),
    ],
    queryFn: async () => {
      if (!params) throw new Error('params required');
      const client = createClient(HealthService, transport(token));
      const request = create(GetHealthTimelineRequestSchema, {
        id: params.id,
        level: params.level,
        from: params.from,
        to: params.to,
        grain: params.grain,
        overallOnly: params.overallOnly ?? false,
      });
      return await client.getHealthTimeline(request);
    },
    enabled: !!token && !!params?.id && enabled,
  });
}

export function useGetServiceHealthStatus(
  token?: string | null,
  workspaceId?: string,
  enabled = true,
) {
  return useQuery({
    queryKey: ['service-health-status', workspaceId ?? ''],
    queryFn: async () => {
      if (!workspaceId) throw new Error('workspaceId required');
      const client = createClient(HealthService, transport(token));
      const request = create(GetServiceHealthStatusRequestSchema, {
        workspaceId,
      });
      return await client.getServiceHealthStatus(request);
    },
    enabled: !!token && !!workspaceId && enabled,
    refetchInterval: 60000, // Refresh every 60 seconds
  });
}

/**
 * Mutation hook to run a live diagnostic against a workload via the
 * sunstone-investigate-controller. A real investigation takes roughly
 * 50-60 seconds, since it's a live LLM agent run, not a cached lookup.
 *
 * @param token - Auth0 access token (required for authenticated endpoints)
 * @returns Mutation result; `data.rootCause` holds the finding on success
 */
export function useInvestigateService(token?: string | null) {
  return useMutation({
    mutationFn: async (workload: string) => {
      const client = createClient(HealthService, transport(token));
      const request = create(InvestigateServiceRequestSchema, { workload });
      return await client.investigateService(request);
    },
  });
}
